import React, { useState } from 'react';
import { BonusCreationStatusType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { useAppStateContext } from 'src/AppState/AppState';
import { useCommon } from 'src/AppState/Common';
import { BonusCreationStore } from 'src/domains/casino/state/AppCasino/store/bonus-creation-store/BonusCreation.store';
import { BonusCreationModel } from 'src/domains/casino/state/AppCasino/store/bonus-creation-store/BonusCreationModel';
import { BonusCreationsTableState } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creations-table/BonusCreationsTable.state';
import { validStatuses } from 'src/domains/casino/webview/bonus-engine/bonus-creator/BonusCreator.state';
import { IconButton } from 'src/domains/common/components/button/icon-button';
import { Select } from 'src/domains/common/components/select/select';
import { TableCell, TableRow } from 'src/domains/common/components/table/table';
import { cn } from 'src/domains/common/components/utils';
import { observer } from 'src/utils/mobx-react';
import { BonusCreationState } from 'src/domains/casino/webview/bonus-engine/bonus-creator/bonus-creation/BonusCreation.state';

function getAvailableStatuses(status: BonusCreationStatusType, currentStatus: string): boolean {
    const statusMap: Record<string, BonusCreationStatusType[]> = {
        draft: ['draft', 'published'],
        published: ['published', 'draft'],
    };

    return statusMap[currentStatus]?.includes(status) ?? false;
}

export interface BonusCreationsTableRowPropsType {
    toggleRowExpansion: (bonusCreationId: string) => void;
    rowClass: string;
    isExpanded: boolean;
    bonusCreation: BonusCreationModel;
}

export const BonusCreationsTableRow = observer(
    'BonusCreationsTableRow',
    ({ toggleRowExpansion, rowClass, isExpanded, bonusCreation }: BonusCreationsTableRowPropsType) => {
        const { routerBackoffice } = useAppStateContext();
        const common = useCommon();
        const [bonusCreationsTableState] = useState(() => new BonusCreationsTableState(common));
        const bonusCreationStore = BonusCreationStore.get(common);

        const isBorder = isExpanded ? 'border-b-0' : 'border-b';
        const bonusCreationState = BonusCreationState.get(common, bonusCreation.id);

        return (
            <React.Fragment>
                <TableRow
                    className={cn(
                        rowClass,
                        isBorder,
                        'data-[state=selected]:bg-muted border-border border-solid border-t-0 border-l-0 border-r-0'
                    )}
                    data-test={`bonusReward-row-${bonusCreation.id}`}
                >
                    <TableCell className='py-0 !px-2 h-14'>
                        <IconButton
                            className='no-hover'
                            variant='ghost'
                            size='default'
                            onClick={() => toggleRowExpansion(bonusCreation.id)}
                            icon={isExpanded ? 'minus' : 'plus'}
                            disabled={bonusCreation.terms === ''}
                            data-test='expand-button'
                        />
                    </TableCell>
                    <TableCell
                        className='h-14'
                        data-test='bonusReward-name'
                    >
                        {bonusCreation.name}
                    </TableCell>
                    <TableCell
                        className='capitalize h-14'
                        data-test='bonusReward-reward-type'
                    >
                        {bonusCreation.rewardTypeForView}
                    </TableCell>
                    <TableCell
                        className={cn({ 'text-destructive': bonusCreation.isExpired }, 'capit alize h-14')}
                        data-test='bonusReward-reward-expire'
                    >
                        {bonusCreation.rewardExpiryDisplay}
                    </TableCell>
                    <TableCell className='h-14'>
                        <Select
                            key={bonusCreation.status}
                            placeholder='Select'
                            values={validStatuses
                                .filter((status) => getAvailableStatuses(status, bonusCreation.status))
                                .map((clickedStatus) => ({
                                    value: clickedStatus,
                                    label: clickedStatus.charAt(0).toUpperCase() + clickedStatus.slice(1),
                                    onClick: async (): Promise<void> => {
                                        if (clickedStatus === bonusCreation.status) return;

                                        if (clickedStatus === 'published') {
                                            await bonusCreationState.activateBonusFromList();
                                            await bonusCreationStore.refreshBonusCreations();
                                        }

                                        if (clickedStatus === 'draft') {
                                            await bonusCreationState.confirmUnpublishBonusCreation();
                                            await bonusCreationStore.refreshBonusCreations();
                                        }
                                    },
                                }))}
                            formInputState={bonusCreation.statusForm}
                            className='w-28'
                            isAutoPosition={true}
                            errors={[]}
                            dataTest='bonusReward-change-status'
                            triggerClassName='[&>span]:w-full [&>span]:select-none'
                        />
                    </TableCell>
                    <TableCell className='h-14'>
                        <div className='flex gap-4'>
                            <IconButton
                                variant='ghost'
                                title='Edit'
                                size='sm'
                                icon='pencil'
                                onClick={() =>
                                    routerBackoffice.redirectToBonusCreation({
                                        type: 'EDIT',
                                        view: 'general-details',
                                        bonusCreationId: bonusCreation.id,
                                    })
                                }
                                data-test='bonusReward-edit-button'
                            />
                            <IconButton
                                icon='copy'
                                onClick={() =>
                                    routerBackoffice.redirectToBonusCreation({
                                        type: 'NEW',
                                        view: 'general-details',
                                        cloneId: bonusCreation.id,
                                    })
                                }
                                size='sm'
                                title='Copy'
                                variant='ghost'
                                data-test='bonusReward-clone-button'
                            />

                            <IconButton
                                className='text-destructive hover:text-destructive'
                                icon='trash'
                                onClick={() =>
                                    bonusCreationsTableState.handleDeleteBonusReward(
                                        bonusCreation.id,
                                        bonusCreation.name
                                    )
                                }
                                size='sm'
                                title='Delete'
                                variant='ghost'
                                disabled={
                                    bonusCreation.status === 'published' ||
                                    bonusCreationsTableState.isLoading === bonusCreation.id
                                }
                                data-test='bonusReward-delete-button'
                            />
                        </div>
                    </TableCell>
                </TableRow>

                {isExpanded && (
                    <TableRow
                        className={cn(rowClass, 'overflow-hidden border-border border-solid border-t-0 border-x-0')}
                        dataTest='bonusReward-details-wrapper'
                    >
                        <TableCell
                            colSpan={2}
                            className='align-top'
                        >
                            <p className='text-muted-foreground'>Bonus Id</p>
                            <p data-test='bonusReward-id'>{bonusCreation.id}</p>
                        </TableCell>
                        <TableCell
                            colSpan={4}
                            className='align-top'
                        >
                            <p className='text-muted-foreground'>Terms & Conditions</p>
                            <p
                                data-test='bonusReward-terms-conditions'
                                className='line-clamp-5'
                                dangerouslySetInnerHTML={{ __html: bonusCreation.terms }}
                            />
                        </TableCell>
                    </TableRow>
                )}
            </React.Fragment>
        );
    }
);
